import {
  IAttachment,
  IChecklistAnswerItem,
  IFormMaterial,
  IFormMaterialResource,
  IMeasure,
  IPermitFieldsInput,
  IRichText,
  ISiteLocation,
  ITablePermitFieldInput,
  PermitFieldType,
} from '~/client/graph'

import {
  locationPermitFieldTypes,
  materialPermitFieldTypes,
  materialResourcePermitFieldTypes,
} from '../constants/PermitFieldTypeConstants'
import PermitField from '../models/PermitField'
import { removeWorkflowConditionals } from './WorkflowFieldsHelper'

const objectIdFieldTypes = [
  PermitFieldType.Company,
  PermitFieldType.Assignee,
  PermitFieldType.Requester,
  PermitFieldType.Subscriber,
  PermitFieldType.User,
  PermitFieldType.CompanyInformational,
  PermitFieldType.LinkedWorkflow,
  PermitFieldType.Activity,
  PermitFieldType.PrimaryActivity,
]
const dateTimeFieldTypes = [PermitFieldType.AdditionalDate]
const stringFieldTypes = [
  PermitFieldType.Select,
  PermitFieldType.InputText,
  PermitFieldType.InputTextArea,
  PermitFieldType.InputNumber,
  PermitFieldType.Email,
  PermitFieldType.PhoneNumber,
  PermitFieldType.Time,
]
const checklistFieldTypes = [
  PermitFieldType.Checklist,
  PermitFieldType.Question,
]

const generalValidator = (value: any): boolean => !!value
// Id only (STX1-597): a location of a kind no LocationType names has no snapshot type to
// send, and requiring one dropped the value and its conditional fields.
const locationValidator = (value: ISiteLocation): boolean => !!value?.id
const measureValidator = (value: IMeasure): boolean =>
  !!value?.units || !!value?.value
const attachmentValidator = (value: IAttachment): boolean =>
  !!value?.size && !!value.url && !!value.fileName
const checklistValidator = (value: IChecklistAnswerItem): boolean =>
  !!value?.checklistItemId
const materialValidator = (value: IFormMaterial): boolean => !!value?.materialId
const materialResourceValidator = (value: IFormMaterialResource): boolean =>
  !!value?.locationId
const richTextValidator = (value: IRichText): boolean =>
  !!value?.content || !!value?.attachments?.length

const dtoTableFieldFilter = (field: PermitField): ITablePermitFieldInput => {
  field.values = (field.values as PermitField[][]).filter(
    row => !!row.length && row.filter(f => f.values.length),
  )
  return {
    instanceId: field.instanceId,
    fieldId: field.fieldId,
    type: field.type,
    parentInstanceId: field.parentInstanceId,
    parentValueIndex: field.parentValueIndex,
    values: (field.values as PermitField[][]).map(fields =>
      getPermitFieldsDto(fields, true, true),
    ),
  }
}

const getFieldsDtos = (
  fields: PermitField[],
  types: PermitFieldType[],
): any[] => {
  return fields.reduce((list, field) => {
    if (
      types.length === 1 &&
      types.includes(field.type) &&
      field.type === PermitFieldType.Table
    ) {
      const tableField = dtoTableFieldFilter(field)
      if (tableField.values.length) {
        list.push(tableField)
      }
      return list
    }

    if (types.includes(field.type)) {
      list.push(field)
    }
    return list
  }, [] as any[])
}

const fieldValidatorMap: Record<PermitFieldType, (value: any) => boolean> = {
  [PermitFieldType.Company]: generalValidator,
  [PermitFieldType.Assignee]: generalValidator,
  [PermitFieldType.Requester]: generalValidator,
  [PermitFieldType.Subscriber]: generalValidator,
  [PermitFieldType.User]: generalValidator,
  [PermitFieldType.CompanyInformational]: generalValidator,
  [PermitFieldType.LinkedWorkflow]: generalValidator,
  [PermitFieldType.Activity]: generalValidator,
  [PermitFieldType.PrimaryActivity]: generalValidator,
  [PermitFieldType.AdditionalDate]: generalValidator,
  [PermitFieldType.Select]: generalValidator,
  [PermitFieldType.InputText]: generalValidator,
  [PermitFieldType.InputTextArea]: generalValidator,
  [PermitFieldType.InputNumber]: generalValidator,
  [PermitFieldType.Email]: generalValidator,
  [PermitFieldType.PhoneNumber]: generalValidator,
  [PermitFieldType.Time]: generalValidator,
  [PermitFieldType.File]: attachmentValidator,
  [PermitFieldType.Location]: locationValidator,
  [PermitFieldType.Equipment]: locationValidator,
  [PermitFieldType.LocationInformational]: locationValidator,
  [PermitFieldType.EquipmentInformational]: locationValidator,
  [PermitFieldType.Checklist]: checklistValidator,
  [PermitFieldType.Question]: checklistValidator,
  [PermitFieldType.Measure]: measureValidator,
  [PermitFieldType.Material]: materialValidator,
  [PermitFieldType.MaterialInformational]: materialValidator,
  [PermitFieldType.MaterialResource]: materialResourceValidator,
  [PermitFieldType.MaterialResourceInformational]: materialResourceValidator,
  [PermitFieldType.RichText]: richTextValidator,
  [PermitFieldType.Signature]: generalValidator,
  // doesn't have conditionals and most likely will not have
  [PermitFieldType.Table]: null,
  // non-savable in fields array
  [PermitFieldType.StartFinishDates]: null,
  [PermitFieldType.Section]: null,
  [PermitFieldType.Subsection]: null,
  [PermitFieldType.Divider]: null,
  [PermitFieldType.Instructions]: null,
  [PermitFieldType.Text]: null,
  [PermitFieldType.InlineMedia]: null,
}

const getSanitizedFields = (
  fields: PermitField[],
  skipConditionalRemoval?: boolean,
): PermitField[] => {
  let updatedFields = [...fields]

  fields.forEach(field => {
    if (!updatedFields.some(f => f.instanceId === field.instanceId)) {
      return
    }
    const isValidFn = fieldValidatorMap[field.type]
    if (!isValidFn) {
      return
    }

    const removedIndexes: number[] = []
    field.values = field.values.reduce((values, value, idx) => {
      if (!isValidFn(value)) {
        removedIndexes.push(idx)
      } else {
        values.push(value)
      }
      return values
    }, [])

    if (!skipConditionalRemoval) {
      removedIndexes.sort((a, b) => b - a)
      removedIndexes.forEach(idx => {
        updatedFields = removeWorkflowConditionals(
          updatedFields,
          field.instanceId,
          idx,
        )
      })
    }
  })

  return updatedFields
}

const getPermitFieldsDto = (
  fields: PermitField[],
  shouldSkipTable?: boolean,
  skipConditionalRemoval?: boolean,
): IPermitFieldsInput => {
  const updatedFields = getSanitizedFields(fields, skipConditionalRemoval)

  return {
    objectIdFields: getFieldsDtos(updatedFields, objectIdFieldTypes),
    dateTimeFields: getFieldsDtos(updatedFields, dateTimeFieldTypes),
    attachmentFields: getFieldsDtos(updatedFields, [PermitFieldType.File]),
    locationFields: getFieldsDtos(updatedFields, locationPermitFieldTypes),
    stringFields: getFieldsDtos(updatedFields, stringFieldTypes),
    checklistAnswerItemFields: getFieldsDtos(
      updatedFields,
      checklistFieldTypes,
    ),
    measureFields: getFieldsDtos(updatedFields, [PermitFieldType.Measure]),
    tableFields: shouldSkipTable
      ? []
      : getFieldsDtos(updatedFields, [PermitFieldType.Table]),
    materialFields: getFieldsDtos(updatedFields, materialPermitFieldTypes),
    materialResourceFields: getFieldsDtos(
      updatedFields,
      materialResourcePermitFieldTypes,
    ),
    richTextFields: getFieldsDtos(updatedFields, [PermitFieldType.RichText]),
    signatureFields: getFieldsDtos(updatedFields, [PermitFieldType.Signature]),
  }
}

export default getPermitFieldsDto
